import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PathCastPlayer = ({ audioUrl }) => {
  const [audio, setAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // When the audioUrl changes, load & autoplay the new track
  useEffect(() => {
    if (!audioUrl) return;

    // Stop any previously playing audio
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    const newAudio = new Audio(audioUrl);
    setAudio(newAudio);

    // Attempt automatic playback
    newAudio
      .play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        console.error('Autoplay failed – user interaction required to start audio', err);
      });

    newAudio.onended = () => setIsPlaying(false);

    // Cleanup when component unmounts / url changes
    return () => {
      newAudio.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  const togglePlayback = () => {
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const downloadAudio = () => {
    if (!audioUrl) return;
    setIsDownloading(true);
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = `PathCast-${Date.now()}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setIsDownloading(false), 500);
  };

  if (!audioUrl) return null;

  return (
    <div className="px-4 w-full mx-auto my-4 bg-black/20 rounded-lg border border-zinc-700 shadow-lg p-2 print:hidden">
      <div className="flex items-center gap-4">
        {/* PathCast label */}
        <div className="text-zinc-100 flex-shrink-0 hidden sm:block mr-2">
          <img
            src="/util/app/b3-admin-podcast-a.svg"
            alt="PathCast"
            className="w-6 h-6 mr-2 inline-block"
          />
          PathCast
        </div>

        {/* Play / Pause button */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={togglePlayback}
          className="min-w-[36px] h-[36px] rounded-md flex items-center justify-center bg-sky-500 hover:bg-sky-400 text-white transition-colors focus:outline-none"
        >
          <AnimatePresence mode="wait">
            {isPlaying ? (
              <motion.svg
                key="pause"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 7a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1zm6 0a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </motion.svg>
            ) : (
              <motion.svg
                key="play"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.25 6.708A1 1 0 007 7.6v4.8a1 1 0 001.5.866l4-2.4a1 1 0 000-1.732l-4-2.4a1 1 0 00-.25-.126z"
                  clipRule="evenodd"
                />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Download button */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={downloadAudio}
          disabled={isDownloading}
          className="min-w-[36px] h-[36px] rounded-md flex items-center justify-center bg-sky-500 hover:bg-sky-400 text-white transition-colors focus:outline-none disabled:opacity-50"
          title="Download PathCast audio"
        >
          <AnimatePresence mode="wait">
            {isDownloading ? (
              <motion.svg
                key="loader"
                className="w-6 h-6 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </motion.svg>
            ) : (
              <motion.svg
                key="download"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="w-6 h-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </div>
  );
};

export default PathCastPlayer; 